import { debounce } from 'lodash';
import { BASE_PATH } from '../constants/admin-paths';
import { formatOptionsForSelect } from './filters-util';
import { themeOptions } from 'constants/generic-lists';
import { TRACKING_URLS } from 'constants/generic-constants';
import { getLocalStorage } from './cookies-util';
import { store } from 'store';
import { getTimezone } from './time/display_time';

export const isEmptyObject = (obj) =>
  obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;

export const isEmptyArray = (arr) => arr && Array.isArray(arr) && arr?.length === 0;

export const isNull = (item) => item === null;

export const searchQuerySetting = () => {
  let searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('page')) {
    searchParams.delete('page');
    const newSearch = `${window.location.pathname}?${decodeURIComponent(searchParams.toString())}`;
    window.location.replace(newSearch);
  }
};

export const getErrorString = (err) => {
  const errorData = err?.response?.data;
  if (!errorData) return err?.response?.statusText || err?.message || err?.response || err?.message?.[0];
  const errorExtracted = errorData?.error || errorData?.message || errorData;

  searchQuerySetting();
  if (typeof errorExtracted === 'string') return errorExtracted;
  if (errorExtracted?.length) return errorExtracted?.join(', ');
};

export const isFieldRestricted = (field) => {
  if (field) {
    return field === 'Restricted';
  } else {
    return false;
  }
};

export const isNumericString = (string) => {
  if (typeof string === 'number' || string === '') return true;
  const regex = /[^0-9.]/;
  return !regex.test(string);
};

export const getCurrencyString = (string) => {
  return `$${string}`;
};

export const isCtrlClicked = (e) => e.ctrlKey || e.metaKey || e.control || e.detail === 2;

export const openUrlLinkInNewTab = (url = '', isAppUrl = false) => {
  const baseUrl = new URL(window.location.href);
  const routeUrl = isAppUrl ? `${BASE_PATH + url}` : url;
  window.open(baseUrl.origin + routeUrl, '_blank').focus();
};

export const fetchValueByDotOperator = (object, value) => {
  if (value?.includes('[array]')) {
    let returnValue = [];
    fetchValueByDotOperator(object, value.match(/^(.*?)\.\[array\]/)?.[1])?.map((v, i) => {
      returnValue?.push(fetchValueByDotOperator(object, value.replace('[array]', `[${i}]`)));
    });
    return returnValue?.join(', ');
  } else {
    return value.split('.').reduce((acc, cur) => {
      let curr = /^\[(\d+)\]$/.test(cur) ? [parseInt(cur.match(/\d+/)[0])] : cur;
      return acc && acc[curr] !== undefined && acc[curr] !== null ? acc[curr] : undefined;
    }, object);
  }
};

export const formateNumberWithCommas = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  return Number(value).toLocaleString('en-US');
};

export const fetchSearchDebounce = debounce(async ({ api = async () => {}, query = '' }) => {
  api({ page: 1, query });
}, 300);

export const infiniteScrollApiCall = async ({ api = async () => {}, query = '', key = '', page = 1 }) => {
  const data = await api(query ? `?page=${page}&${query}` : `?page=${page}`);
  if (data[key]) {
    return { list: data[key], current_page: data?.meta?.pagination?.page, total_pages: data?.meta?.pagination?.pages };
  } else {
    return { list: [], current_page: null, total_pages: null };
  }
};

export const getCategoriesValuesForSelect = (sourceCategories = []) => {
  return formatOptionsForSelect({
    list: sourceCategories?.map((category) => {
      return {
        ...category?.category,
        source_id: category?.id,
      };
    }),
  });
};

export const getMergedValuesForDestroy = ({
  orignalValues = [],
  updatedValues = [],
  updated_source_key = 'id',
  orignal_source_key = 'id',
}) => {
  // Step 1: Build a quick lookup map for original values
  const originalMap = new Map(orignalValues.map((val) => [val?.[orignal_source_key], val]));

  const final = [];
  const usedOriginalIds = new Set();

  // Step 2: Follow updatedValues sequence exactly
  updatedValues.forEach((updated) => {
    const updatedId = updated?.[updated_source_key];

    if (updatedId && originalMap.has(updatedId)) {
      // Existing record → updated version comes in this exact order
      final.push(updated);
      usedOriginalIds.add(updatedId);
    } else {
      // New record (no ID or ID not found in original)
      final.push(updated);
    }
  });

  // Step 3: Find original items that were removed
  orignalValues.forEach((original) => {
    const id = original?.[orignal_source_key];
    if (!usedOriginalIds.has(id)) {
      // This was removed → mark _destroy
      final.push({
        ...original,
        _destroy: true,
      });
    }
  });

  return final;
};

export const formatLabel = (input) =>
  input
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const convertToCamelCase = (input) => {
  return input
    .toLowerCase()
    .split('_')
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join('')
    .replace(/s$/, '');
};

export const objectToFormData = (obj, appendKeys = false, formData = new FormData(), parentKey = '') => {
  for (const [key, value] of Object.entries(obj)) {
    const formKey = parentKey ? `${parentKey}[${key}]` : key;
    if (value instanceof Date) {
      formData.append(formKey, value.toISOString());
    } else if (value instanceof File) {
      formData.append(formKey, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const arrayKey = appendKeys ? `${formKey}[${index}]` : `${formKey}[]`;
        if (item instanceof File) {
          // Specifically handle File instances in arrays
          formData.append(arrayKey, item);
        } else if (typeof item === 'object' && item !== null) {
          objectToFormData(item, appendKeys, formData, arrayKey);
        } else {
          formData.append(arrayKey, item);
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      objectToFormData(value, appendKeys, formData, formKey);
    } else {
      formData.append(formKey, value);
    }
  }
  return formData;
};
export const userType = () => window.location.pathname.split('/')?.[1];

export const currentBaseRoute = () => `/${userType()}`;

export const capitalizeFirstLetter = (str) => {
  return str
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const isSmallScreen = () => window.innerWidth < 768;

export const getOrderStatusColor = (name) => {
  switch (name) {
    case 'Charged':
      return 'green';
    case 'Refunded':
      return 'red';
    case 'Approved':
      return 'blue';
    case 'Awaiting Intake':
      return 'orange';
    case 'Cancelled':
      return 'red';
    case 'Active':
      return 'blue';
    default:
      return 'gray';
  }
};
export const isValidJSON = (str) => {
  try {
    const fixedStr = str.replace(/"\s*=>\s*"/g, '":"');
    const parsed = JSON.parse(fixedStr);
    return { isValid: true, data: parsed };
  } catch (error) {
    return { isValid: false, data: null };
  }
};

export const filterBySlug = (input, option) => {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
};

export const convertToFeetEquivalent = (value = 0) => {
  const arr = value?.toString()?.split('.');
  if (arr?.[1]) {
    if (Number(arr?.[1]) >= 12) {
      return Number(arr?.[0]) + 1;
    }
    return Number([arr?.[0], (arr?.[1] / 12)?.toString()?.split('.')?.[1]]?.join('.'));
  } else {
    return value;
  }
};

export const convertFromFeetEquivalent = (value = 0) => {
  const arr = Number(value)?.toFixed(2).toString()?.split('.');
  if (arr?.[1]) {
    const second = Number((Number(arr?.[1]) * 0.12)?.toFixed(0));
    return [arr?.[0], second < 12 ? second : second?.[0]]?.join('.');
  } else {
    return value;
  }
};

export const fixBase64 = (base64) => {
  let fixed = base64.replace(/-/g, '+').replace(/_/g, '/');
  while (fixed.length % 4 !== 0) {
    fixed += '=';
  }
  return fixed;
};

export const getAdvanceKey = (key) => {
  if (!key) return '';
  const str = key;
  const matches = str.match(/\[(.*?)\]/g);
  const keys = matches.map((k) => k.replace(/\[|\]/g, ''));
  return keys?.[1];
};

export const isDateString = (str) => {
  const isoFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  if (!isoFormat.test(str)) return false;
  const date = new Date(str);
  return !isNaN(date.getTime());
};

export const toUTCString = (localDateStr) => {
  const timeZone = getTimezone();
  const date = new Date(localDateStr);
  const tzDateStr = date.toLocaleString('en-US', { timeZone });
  const tzDate = new Date(tzDateStr);
  const diff = date.getTime() - tzDate.getTime();
  const utcDate = new Date(date.getTime() + diff);

  return utcDate.toISOString();
};

export const makeLinksClickable = (text) => {
  if (!text) return '';
  const anchors = [];
  const textWithoutAnchors = text.replace(/<a\b[^>]*>.*?<\/a>/gis, (match) => {
    anchors.push(match);
    return `__ANCHOR_${anchors.length - 1}__`;
  });
  const linkedText = textWithoutAnchors.replace(
    /(\bhttps?:\/\/[^\s<]+)/g,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );
  return linkedText.replace(/__ANCHOR_(\d+)__/g, (_, index) => anchors[index]);
};

export const isTextContainLink = (text) => (text ? text.match(/\bhttps?:\/\/[^\s]+/g) : false);

export const extractLastPart = (str) => {
  const parts = str.split('\\');
  const className = parts[parts.length - 1];
  const formattedClassName = className.replace(/([a-z])([A-Z])/g, '$1 $2');
  return formattedClassName;
};

export const isMatch = (var1, var2) => JSON.stringify(var1) === JSON.stringify(var2);

export const shortenText = (text = '', limit = 12) => {
  const displayText = text.length > limit ? text.slice(0, limit) + '...' : text;
  const isTextOverflowed = text.length > limit;
  return { displayText, isTextOverflowed };
};

export const isLightTheme = (themeMode = '') => themeMode === 'light';

export const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const downloadBlob = async (blobUrl, filename) => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.click();

  URL.revokeObjectURL(url);
};

export const changeTheme = (themeMode) => {
  const selectedcolorConfig = themeOptions()?.find((opt) => opt?.name === themeMode)?.colorConfig;
  const colors = selectedcolorConfig;
  for (const item in colors) {
    document.documentElement.style.setProperty(item, selectedcolorConfig?.[item]);
  }
  document.documentElement.style.setProperty('color-scheme', themeMode === 'dark' ? 'dark' : 'light');
};

export const generateUUID = () => {
  var dt = new Date().getTime();
  var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};

export const convertCmToFeetInches = (cm) => {
  if (typeof Number(cm) !== 'number' || isNaN(cm) || cm < 0) {
    return 'Invalid input';
  }

  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);

  return `${feet}'${inches}"`;
};

export const feetInchesToCm = (feet, inches) => {
  return feet * 30.48 + inches * 2.54;
};

export const makeTrackingUrl = (courier, trackingNo) => TRACKING_URLS?.[courier]?.replace('#{tracking_no}', trackingNo);

export const turnEmptyArrayIntoNull = (arr = []) => (arr?.length === 0 ? null : arr);

export const isObject = (item) => {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
};

export const getProductName = ({ variant = {}, seperator = '/' }) => {
  const productType = variant?.product?.type;
  const isInternal = productType?.slug === 'internal';
  const variantName = variant?.name || '';
  const productName = isInternal ? `${variant?.product?.name} ${seperator} ${variantName}` : variantName;
  return productName;
};

export const toTitle = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const slugify = (str = '') => {
  return String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

export const formatNumber = (value) => {
  const num = Number(value);
  return num % 1 === 0 ? Math.trunc(num).toString() : num?.toFixed(2).toString();
};

export const combineObjectValues = (obj, separator = ', ') => {
  if (!obj || typeof obj !== 'object') return '';

  const values = Object.values(obj).filter((val) => val !== undefined && val !== null && val !== '');

  return values.join(separator);
};

export const transformProducts = (data = {}) => {
  const result = [];

  if (!data) return [];

  Object?.entries(data)?.forEach(([key, value]) => {
    const match = key.match(/^(.+)_(\d+)$/);
    if (match) {
      const [, baseKey, index] = match;
      const idx = parseInt(index) - 1;

      if (!result[idx]) result[idx] = {};
      result[idx][baseKey] = value;
    }
  });

  return result.filter(Boolean);
};

export const removeSearchParam = (param = '') => {
  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams?.has(param)) return;

  searchParams.delete(param);
  const newUrl = `${window.location.pathname}?${searchParams.toString()}`;

  window.history.replaceState({}, '', newUrl);
};

export const getCurrentThemeColors = () => {
  const theme = getLocalStorage('theme') || getSystemTheme() || 'light';
  return themeOptions()?.find((opt) => opt?.name === theme)?.colorConfig;
};

export const sanitizeFileNameForPresignedURL = ({ fileName = '' }) => {
  return fileName ? fileName.replace(/[^a-zA-Z0-9.\- ]/g, '') : '';
};

export const isDateSame = (d1 = '', d2 = '') => {
  const date1 = d1.split('T')[0];
  const date2 = d2.split('T')[0];

  return date1 === date2;
};

export const getCurrentTenant = () => store?.getState()?.tenantConfigData?.tenantData?.slug;

export const getCurrentTenantSupportEmail = () => store?.getState()?.tenantConfigData?.tenantData?.support_email;

export const extractTextFromJson = (json = '') => {
  if (!json || typeof json !== 'string') return '';

  try {
    // fix key quotes
    let fixed = json.replace(/([{,]\s*)'([^']+)'\s*:/g, '$1"$2":');

    // fix value quotes if wrapped in single quotes
    fixed = fixed.replace(/:\s*'([^']*)'/g, (_, val) => {
      return `:"${val.replace(/"/g, '\\"')}"`;
    });

    const parsed = JSON.parse(fixed);
    const html = parsed?.htmlContent ?? '';

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent?.trim() ?? '';
  } catch (err) {
    console.error('Invalid JSON:', err);
    return '';
  }
};

export const calculateTotalAmount = ({ values = [], precision = 1 }) => {
  const total = values.reduce((sum, currentValue) => {
    return sum + parseFloat(currentValue || 0);
  }, 0);

  return total.toFixed(precision);
};

export const parseAddressComponents = ({
  place = {},
  setFieldValue = () => {},
  addressSuffix = 'address',
  citySuffix = 'city',
  countrySuffix = 'country',
  postalCodeSuffix = 'postal_code',
  stateSuffix = 'state',
  defaultStates,
}) => {
  const { states: filterStates = [] } = store.getState().filtersData || {};
  const states = defaultStates ?? filterStates;
  const components = place?.address_components;

  const getComponent = (type) => components.find((c) => c.types.includes(type));

  const streetNumber = getComponent('street_number')?.long_name || getComponent('intersection')?.long_name;
  const route =
    getComponent('route')?.long_name ||
    getComponent('sublocality')?.long_name ||
    getComponent('sublocality_level_1')?.long_name;

  const city = getComponent('locality')?.long_name || '';

  const stateLong = getComponent('administrative_area_level_1')?.long_name || '';

  const stateShort = getComponent('administrative_area_level_1')?.short_name || '';

  const country = getComponent('country')?.long_name || '';

  const postalCode = getComponent('postal_code')?.long_name || '';

  if (city) setFieldValue(citySuffix, city);
  if (postalCode) setFieldValue(postalCodeSuffix, postalCode);
  if (country) setFieldValue(countrySuffix, country);

  setFieldValue(addressSuffix, [streetNumber, route].filter(Boolean).join(' '));

  const matched = states?.find(
    (opt) => opt?.slug?.toLowerCase() === stateShort?.toLowerCase() || opt?.name === stateLong,
  );
  if (matched) setFieldValue(stateSuffix, [matched]);

  return {
    address: [streetNumber, route].filter(Boolean).join(' '),
    city,
    state: {
      short: stateShort,
      long: stateLong,
    },
    country,
    postalCode,
  };
};

export const getOrderId = ({ data = {} }) => {
  const isSubscription = !!data?.subscription?.id;
  if (data?.reference_number) return data.reference_number;

  if (isSubscription) return data?.subscription?.external_id ?? data?.subscription?.id;

  return data?.external_id ? data?.external_id : data?.id;
};

export const toObject = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

export const toSnakeCase = (str) => {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
};

export const buildThemedHtmlDoc = (html = '', themeMode = 'light') => {
  if (!html) return '';

  const isDark = !isLightTheme(themeMode);

  let doc = html;

  const buttonFix = isDark ? `a[style*="background"]{color:#ffffff!important;}` : '';
  const injectedStyle =
    `<style>` + `html,body{margin:0!important;overflow-x:hidden!important;}` + buttonFix + `</style>`;
  doc = /<\/head>/i.test(doc) ? doc.replace(/<\/head>/i, `${injectedStyle}</head>`) : injectedStyle + doc;

  return doc;
};

export const isAllContainOneVariant = (data) => data?.every((entry) => (entry?.product_variants || [])?.length < 2);

export const modifyProductVariantActiveColumnWidth = ({ data = [], activeColumns = [] }) => {
  const allContainOneVariant = isAllContainOneVariant(data);
  return activeColumns?.map((column) =>
    ['product_variants', 'order_product_variants'].includes(column?.sortfield)
      ? { ...column, min_width: allContainOneVariant ? 300 : 600 }
      : column,
  );
};

export const formatSize = (bytes) => {
  if (bytes < 1024) {
    return bytes + ' B';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
};
